import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import { addToUser } from "../../redux/actions/userAction";
import { AppInstance } from "../../api/instance";
import setNewFormData from '../../api/setNewFormData';
import './AuthModal.scss';

const AuthModal = ({ isOpen, onClose, onSuccess, defaultTab = 'login' }) => {
  const [currentScreen, setCurrentScreen] = useState('login');
  const [loginMethod, setLoginMethod] = useState('otp');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    otp: '',
    country_code: '+971'
  });
  const [otpInputs, setOtpInputs] = useState(['', '', '', '', '', '']);
  const [onSave, setOnSave] = useState(false);
  const [formError, setFormError] = useState({});
  const [otpTimer, setOtpTimer] = useState(0);
  const [phoneForOtp, setPhoneForOtp] = useState('');
  const [inputType, setInputType] = useState('');
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [signupId, setSignupId] = useState('');
  const addToast = () => {};
  const dispatch = useDispatch();

  // Country codes array
  // const countryCodes = [
  //   { code: '+971', country: 'UAE' },
  //   { code: '+1', country: 'USA' },
  //   { code: '+44', country: 'UK' },
  //   { code: '+91', country: 'India' },
  //   { code: '+86', country: 'China' },
  //   { code: '+81', country: 'Japan' },
  //   { code: '+49', country: 'Germany' },
  //   { code: '+33', country: 'France' },
  //   { code: '+61', country: 'Australia' },
  //   { code: '+65', country: 'Singapore' }
  // ];

  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Update activeTab when defaultTab changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // Handle modal open/close effects
  useEffect(() => {
    if (isOpen) {
      // Close any open dropdowns
      const dropdowns = document.querySelectorAll('.account-dropdown, .search-content, .shopping-cart-content');
      dropdowns.forEach(dropdown => {
        dropdown.classList.remove('active');
      });
    }
  }, [isOpen]);



  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formError[name]) {
      setFormError(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Detect input type for phone/email field
    if (name === 'phone' || name === 'email') {
      const inputValue = value.trim();
      if (inputValue) {
        // Check if input starts with number (phone) or letter (email)
        if (/^\d/.test(inputValue)) {
          setInputType('phone');
          setShowCountrySelect(true); // Always show country code for phone
        } else if (/^[a-zA-Z]/.test(inputValue)) {
          setInputType('email');
          setShowCountrySelect(false); // Hide country code for email
        }
      } else {
        setInputType('');
        setShowCountrySelect(false);
      }
    }
  };

  const handleOtpInputChange = (index, value) => {

    if (value.length > 1) return;

    const newOtpInputs = [...otpInputs];
    newOtpInputs[index] = value;
    setOtpInputs(newOtpInputs);

    const otpValue = newOtpInputs.join('');
    setFormData(prev => ({
      ...prev,
      otp: otpValue
    }));

    if (formError.otp) {
      setFormError(prev => ({
        ...prev,
        otp: ''
      }));
    }

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpInputs[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };


  const startOtpTimer = () => {
    setOtpTimer(60);
  };

  const handleSendOtp = async (data) => {
    setOnSave(true);
    const isOtpAlreadySentMessage = (message) =>
      String(message || "").toLowerCase().includes("otp already sent");
    try {
      const username = data.phone || data.email;

      if (activeTab === 'login') {
        const loginData = {
          username: username,
          type: 'otp',
          platform: 'web'
        };
        const response = await AppInstance({
          method: "post",
          url: "/login",
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          data: setNewFormData(loginData)
        });

        if (response.data?.success !== false) {
          setPhoneForOtp(username);
          setCurrentScreen('otp');
          startOtpTimer();
        } else {
          const apiMessage = response.data?.message || "";
          if (isOtpAlreadySentMessage(apiMessage)) {
            setPhoneForOtp(username);
            setCurrentScreen('otp');
            startOtpTimer();
            setFormError({});
            return;
          }
          // Handle API response errors (e.g. User not found)
          const errorMessage = apiMessage || "Failed to send OTP. Please try again.";
          setFormError({ phone: errorMessage });
        }
      } else {
        // Register flow - call /signup/otp endpoint
        let requestData = {
          country_code: data.country_code || '+971',
          type: inputType || 'phone' // 'email' or 'phone'
        };

        // If type is email, pass email in email key, otherwise pass phone in phone key
        if (inputType === 'email') {
          requestData.email = username;
          requestData.phone = '';
        } else {
          requestData.phone = username;
          requestData.email = '';
        }

        const response = await AppInstance({
          method: "post",
          url: "/signup/otp",
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          data: setNewFormData(requestData)
        });

        if (response.data?.success) {
          setPhoneForOtp(username);
          setSignupId(response.data.signup_id || ''); // Store signup_id for verification
          setCurrentScreen('otp');
          startOtpTimer();
        } else {
          // Handle API response errors
          const errorMessage = response.data?.message || "Failed to send OTP. Please try again.";
          setFormError({ phone: errorMessage });
        }
      }
    } catch (error) {
      let errorMessage = "Something went wrong. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        const username = data.phone || data.email;
        if (isOtpAlreadySentMessage(errorMessage) && activeTab === "login") {
          setPhoneForOtp(username);
          setCurrentScreen('otp');
          startOtpTimer();
          setFormError({});
          return;
        }
      } else if (error.response?.data?.errors?.email
        || error.response?.data?.errors?.phone) {
        errorMessage = error.response.data.errors.email
          || error.response.data.errors.phone;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid request. Please check your input.";
      } else if (error.response?.status === 422) {
        errorMessage = "Invalid data format. Please check your input.";
      }
      setFormError({ phone: errorMessage });
    } finally {
      setOnSave(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setFormError({});

    if (!formData.phone && !formData.email) {
      setFormError({ phone: "Phone number or email is required" });
      return;
    }

    await handleSendOtp(formData);
  };



  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!formData.otp) {
      setFormError({ otp: "OTP is required" });
      return;
    }

    // Clear previous errors
    setFormError({});

    setOnSave(true);
    try {
      if (activeTab === 'register') {
        // Handle registration with OTP - call /signup/otp/verify
        const verifyData = {
          otp_code: formData.otp,
          signup_id: signupId,
          platform: 'web'
        };

        const response = await AppInstance({
          method: "post",
          url: "/signup/otp/verify",
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          data: setNewFormData(verifyData)
        });

        if (response.data?.success) {
          // API may return token/user at response.data or response.data.data
          const authData = response.data.data || response.data;
          const token = authData.token || response.data.token;
          // Auto-login after successful registration when token is present
          if (token) {
            localStorage.setItem("token", token);
            dispatch(addToUser(authData, addToast));
            onSuccess && onSuccess();
            onClose();
          } else {
            setActiveTab('login');
            setCurrentScreen('login');
            setFormData({ phone: '', email: '', password: '', fname: '', lname: '', otp: '', country_code: '+971' });
            setInputType('');
            setShowCountrySelect(false);
            setOtpInputs(['', '', '', '', '', '']);
            setSignupId('');
          }
        } else {
          // Handle API response errors (invalid OTP, etc.)
          const errorMessage = response.data?.message || "Invalid OTP. Please try again.";
          setFormError({ otp: errorMessage });
        }
      } else {
        // Handle login with OTP - call /login/otp/verify
        const loginData = {
          username: phoneForOtp,
          otp_code: formData.otp,
          platform: 'web'
        };

        const response = await AppInstance({
          method: "post",
          url: "/login/otp/verify",
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          data: setNewFormData(loginData)
        });

        if (response.data?.success) {
          localStorage.setItem("token", response.data.token);
          dispatch(addToUser(response.data, addToast));
          onSuccess && onSuccess();
          onClose();
        } else {
          // Handle API response errors (invalid OTP, etc.)
          const errorMessage = response.data?.message || "Invalid OTP. Please try again.";
          setFormError({ otp: errorMessage });
        }
      }
    } catch (error) {
      // Handle network errors or other exceptions
      let errorMessage = "Something went wrong. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid OTP. Please check and try again.";
      } else if (error.response?.status === 401) {
        errorMessage = "OTP has expired. Please request a new one.";
      } else if (error.response?.status === 422) {
        errorMessage = "Invalid OTP format. Please enter 6 digits.";
      }

      setFormError({ otp: errorMessage });
    } finally {
      setOnSave(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!formData.password) {
      setFormError({ password: "Password is required" });
      return;
    }

    // Clear previous errors
    setFormError({});

    setOnSave(true);
    try {
      const passwordData = {
        username: phoneForOtp,
        password: formData.password,
        type: 'password',
        platform: 'web'
      };

      const response = await AppInstance({
        method: "post",
        url: "/login",
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        data: setNewFormData(passwordData)
      });

      if (response.data?.success) {
        localStorage.setItem("token", response.data.token);
        dispatch(addToUser(response.data, addToast));
        onSuccess && onSuccess();
        onClose();
      } else {
        // Handle API response errors (invalid password, etc.)
        const errorMessage = response.data?.message || "Invalid password. Please try again.";
        setFormError({ password: errorMessage });
      }
    } catch (error) {
      // Handle network errors or other exceptions
      let errorMessage = "Something went wrong. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = "Invalid password. Please check and try again.";
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid credentials. Please try again.";
      }

      setFormError({ password: errorMessage });
    } finally {
      setOnSave(false);
    }
  };

  const handleResendOtp = () => {
    if (otpTimer === 0) {
      const data = {
        phone: phoneForOtp,
        email: phoneForOtp,
        country_code: formData.country_code
      };
      handleSendOtp(data);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentScreen('login');
    setFormData({ phone: '', email: '', otp: '', country_code: '+971' });
    setFormError({});
    setOtpTimer(0);
    setInputType('');
    setShowCountrySelect(false);
    setLoginMethod('otp');
    setOtpInputs(['', '', '', '', '', '']);
    setSignupId('');
  };

  const handleBackToLogin = () => {
    setCurrentScreen('login');
    setFormData({ phone: '', email: '', otp: '', country_code: '+971' });
    setFormError({});
    setOtpTimer(0);
    setInputType('');
    setShowCountrySelect(true);
    setLoginMethod('otp');
    setOtpInputs(['', '', '', '', '', '']);
    setSignupId('');
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <span>×</span>
        </button>

        {/* Header with tabs */}
        {currentScreen !== 'otp' && (
          <div className="auth-modal-header mt-5">
            <div className="auth-tabs">
              <button
                className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                onClick={() => handleTabChange('login')}
              >
                Login
              </button>
              <button
                className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
                onClick={() => handleTabChange('register')}
              >
                Register
              </button>
            </div>
          </div>)}

        {/* Content */}
        <div className="auth-modal-content">
          {currentScreen === 'login' && (
            <div className="auth-screen">
              <h2 className="auth-title">Hala! Let's get started</h2>

              {/* General error message display */}
              {formError.phone && (
                <div className="error-message-container">
                  <div className="error-icon">⚠️</div>
                  <span className="error-message">{formError.phone}</span>
                </div>
              )}

              <form onSubmit={handleLoginSubmit}>
                <div className="form-group">
                  <div className="input-wrapper">
                    {showCountrySelect && (
                      <div className="country-code-display">
                        <span className="country-code-text">+971</span>
                      </div>
                    )}
                    <input
                      type="text"
                      name="phone"
                      placeholder="Please enter email or mobile number"
                      value={formData.phone}
                      onChange={handleFieldChange}
                      className={`${formError.phone ? 'error' : ''} ${showCountrySelect ? 'with-country' : ''}`}
                    />
                  </div>
                </div>
                <button type="submit" className="btn-primary" style={{ padding: '11px 24px' }} disabled={onSave}>
                  {onSave ? 'Sending...' : 'CONTINUE'}
                </button>
              </form>
              <p className="privacy-text">
                By continuing, I confirm that I have read the{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </p>
            </div>
          )}
          {currentScreen === 'otp' && (
            <div className="auth-screen">
              <div className="otp-header">
                <button className="back-btn" onClick={handleBackToLogin}>
                  ← Back
                </button>
                {/* <div className="otp-tabs">
                  {inputType === 'phone' && (
                    <button
                      className={`otp-tab ${loginMethod === 'otp' ? 'active' : ''}`}
                      onClick={() => setLoginMethod('otp')}
                    >
                      Login with OTP
                    </button>
                  )}
                  <button
                    className={`otp-tab ${loginMethod === 'password' ? 'active' : ''}`}
                    onClick={() => setLoginMethod('password')}
                  >
                    {inputType === 'email' ? 'Login with Password' : 'Login with Password'}
                  </button>
                </div> */}
              </div>

              {loginMethod === 'otp' ? (
                <div className="otp-content">
                  <h2 className="auth-title">Enter OTP</h2>
                  <p className="otp-subtitle">
                    We've sent a 6-digit code to {phoneForOtp}
                  </p>

                  {/* General error message display */}
                  {formError.otp && (
                    <div className="error-message-container">
                      <div className="error-icon">⚠️</div>
                      <span className="error-message">{formError.otp}</span>
                    </div>
                  )}

                  <form onSubmit={handleOtpSubmit}>
                    <div className="otp-inputs-container">
                      {otpInputs.map((value, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]"
                          maxLength="1"
                          value={value}
                          onChange={(e) => handleOtpInputChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className={`otp-input ${formError.otp ? 'error' : ''}`}
                          autoComplete="off"
                        />
                      ))}
                    </div>
                    <button type="submit" className="btn-primary" disabled={onSave}>
                      {onSave ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </form>
                  <div className="otp-footer">
                    <span className="timer">
                      {otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Didn\'t receive code?'}
                    </span>
                    <button
                      className="resend-btn"
                      onClick={handleResendOtp}
                      disabled={otpTimer > 0}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              ) : (
                <div className="password-content">
                  <h2 className="auth-title">
                    {inputType === 'email' ? 'Enter Password' : 'Enter Password'}
                  </h2>
                  <p className="password-subtitle">
                    {inputType === 'email'
                      ? `Login to your account with password`
                      : `Login to your account with password`
                    }
                  </p>

                  {/* General error message display */}
                  {formError.password && (
                    <div className="error-message-container">
                      <div className="error-icon">⚠️</div>
                      <span className="error-message">{formError.password}</span>
                    </div>
                  )}

                  <form onSubmit={handlePasswordLogin}>
                    <div className="form-group">
                      <input
                        type="password"
                        name="password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleFieldChange}
                        className={formError.password ? 'error' : ''}
                      />
                    </div>
                    <button type="submit" className="btn-primary" disabled={onSave}>
                      {onSave ? 'Logging in...' : 'Login'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  defaultTab: PropTypes.oneOf(['login', 'register'])
};

export default AuthModal;
